"use client";
import { useState, useEffect, useMemo, Suspense, useCallback } from 'react';
import Link from 'next/link';
import './quizzes-page.css';
import QuizCard from './components/QuizCard';
import { getQuizzesList, getLeaderboard, getMyQuizAttempts } from './helpers/quizApi';
import { normalizeQuizFlag } from './helpers/quizBooleans';
import { quizzes as fallbackQuizzes } from './quizzesData';
import LazyComponent from '@/app/components/LazyComponent';
import Loader from '@/app/components/loader/loader';
import { EmblaCarousel } from '@/app/components/sliders/embala';
import FloatingBugReport from '@/app/components/support/FloatingBugReport';

const AVATAR_PLACEHOLDER = 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 80 80"><rect fill="#e9ecef" width="80" height="80"/></svg>');

export default function QuizzesPage() {
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('الكل');
  const [searchTerm, setSearchTerm] = useState('');
  const [leaderboardItems, setLeaderboardItems] = useState([]);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);
  const [myAttempts, setMyAttempts] = useState([]);
  const [myAttemptsLoading, setMyAttemptsLoading] = useState(false);
  const [myAttemptsError, setMyAttemptsError] = useState(null);
  const [myAttemptsTotal, setMyAttemptsTotal] = useState(0);
  const [myAttemptsTotalPages, setMyAttemptsTotalPages] = useState(0);
  const [attemptsPage, setAttemptsPage] = useState(1);
  const [trialsFilter, setTrialsFilter] = useState('all');
  const [hasAuthToken, setHasAuthToken] = useState(false);
  const [mainTab, setMainTab] = useState('quizzes');
  const categories = ['الكل'];
  // const categories = ['الكل', 'مهارات', 'أدوات'];

  useEffect(() => {
    if (typeof window === 'undefined') return;
    setHasAuthToken(!!localStorage.getItem('token'));
  }, []);

  const fetchMyAttempts = useCallback(async () => {
    if (!hasAuthToken || mainTab !== 'results') {
      setMyAttempts([]);
      setMyAttemptsTotal(0);
      setMyAttemptsTotalPages(0);
      return;
    }
    setMyAttemptsLoading(true);
    setMyAttemptsError(null);
    try {
      const res = await getMyQuizAttempts({
        page: attemptsPage,
        per_page: 20,
        status: trialsFilter,
      });
      setMyAttempts(Array.isArray(res?.items) ? res.items : []);
      setMyAttemptsTotal(Number(res?.total) || 0);
      setMyAttemptsTotalPages(Number(res?.totalPages) || 0);
    } catch (err) {
      if (err?.status === 401 || err?.code === 'not_authenticated') {
        setMyAttempts([]);
        setMyAttemptsTotal(0);
        setMyAttemptsTotalPages(0);
        setHasAuthToken(false);
      } else {
        setMyAttemptsError(err?.message || 'تعذر تحميل المحاولات');
        setMyAttempts([]);
      }
    } finally {
      setMyAttemptsLoading(false);
    }
  }, [hasAuthToken, attemptsPage, trialsFilter, mainTab]);

  useEffect(() => {
    fetchMyAttempts();
  }, [fetchMyAttempts]);

  // Fetch quizzes from API
  useEffect(() => {
    const fetchQuizzes = async () => {
      try {
        setLoading(true);
        setError(null);

        const apiResponse = await getQuizzesList({
          per_page: 50,
          page: 1,
          search: searchTerm || undefined
        });

        console.log('Fetched quizzes from API:', apiResponse);

        // API returns { items: [...], page: 1, perPage: 50, total: 1, totalPages: 1 }
        // Extract the items array from the response
        const apiQuizzes = apiResponse?.items || (Array.isArray(apiResponse) ? apiResponse : []);

        // Map API response to match QuizCard expected format
        const mappedQuizzes = apiQuizzes.map(quiz => ({
          id: quiz.id, // WordPress post ID
          wordpressId: quiz.id, // Same as id for API calls
          title: quiz.title || 'بدون عنوان',
          description: quiz.excerpt || '',
          icon: 'bi-grid-3x3-gap-fill', // Default icon, can be enhanced later
          duration: 25, // Default, can be calculated or added to API
          questions: quiz.totalQuestions || 0,
          participants: 0, // Not in API response, can be added later
          points: 0,
          category: 'مهارات', // Default, can be added to API
          featuredImage: quiz.featuredImage,
          createdAt: quiz.createdAt,
          expirationAt: quiz.expirationAt ?? null,
          isPopular: false, // Can be determined by logic or added to API
          ramadan: normalizeQuizFlag(quiz.ramadan),
          preview: normalizeQuizFlag(quiz.preview),
        }));

        setQuizzes(mappedQuizzes);
      } catch (err) {
        console.error('Error fetching quizzes:', err);
        setError(err.message);
        // Fallback to dummy data if API fails
        setQuizzes(fallbackQuizzes);
      } finally {
        setLoading(false);
      }
    };

    fetchQuizzes();
  }, [searchTerm]);

  useEffect(() => {
    let cancelled = false;
    const fetchLeaderboard = async () => {
      setLeaderboardLoading(true);
      try {
        const res = await getLeaderboard({ type: 'weekly', limit: 50 });
        if (cancelled) return;
        const raw = Array.isArray(res?.items) ? res.items : [];
        // const excludedRoles = [];
        const excludedRoles = ['commadmin', 'administrator'];
        const isExcluded = (item) => excludedRoles.includes(item.userRole);
        const filtered = raw.filter((item) => !isExcluded(item));
        const items = filtered
          .map((item) => {
            const excludedAhead = raw.filter((r) => isExcluded(r) && r.rank < item.rank).length;
            return { ...item, rank: item.rank - excludedAhead };
          })
          .slice(0, 10);
        setLeaderboardItems(items);
      } catch {
        if (!cancelled) setLeaderboardItems([]);
      } finally {
        if (!cancelled) setLeaderboardLoading(false);
      }
    };
    fetchLeaderboard();
    return () => { cancelled = true; };
  }, []);

  const leaderboardSlides = useMemo(() => {
    return leaderboardItems.map((item) => {
      const attempts = Number(item.totalAttempts) || 0;
      const passed = Number(item.quizzesPassed) || 0;
      const avg = Number(item.averageCompletionPct) || 0;
      const attemptsWord = attempts === 1 ? 'محاولة' : 'محاولات';
      const passedWord = passed === 1 ? 'اختبار مجتاز' : 'اختبارات مجتازة';
      return {
        id: item.userId,
        title: `${item.name} — #${item.rank}`,
        // subtitle: `${passed} ${passedWord} . ${attempts} ${attemptsWord}`,
        subtitle: `${passed} ${passedWord} · متوسط الإنجاز: ${avg.toFixed(1)}%`,
        featuredImage: { node: { sourceUrl: item.avatar || AVATAR_PLACEHOLDER } },
      };
    });
  }, [leaderboardItems]);

  const filteredQuizzes = selectedCategory === 'الكل'
    ? quizzes
    : quizzes.filter(quiz => quiz.category === selectedCategory);

  return (
    <main className="container py-2" dir="rtl">
      {/* Header */}
      <div className="text-center mb-5">
        <h1
          className="display-4 fw-bold mb-3"
        >
          الاختبارات
        </h1>
        <p className="lead text-muted mx-auto" style={{ maxWidth: '700px' }}>
          اكتشف مستواك واحصل على توصيات تعليمية مخصصة وقارن قدراتك مع المصممين حول العالم
        </p>
      </div>

      {/* <div className="my-0 my-md-5 pb-4 mx-auto col-12 fade-in-up">
        <h2 className="ms-2 ms-md-2 mb-3">
          <i className="bi bi-trophy" aria-hidden="true" /> لوحة المتصدرين الأسبوعية ( Top 10 )
        </h2>
        <p className="text-muted small ms-2 ms-md-2 mt-n2 mb-3">
          في حال تساوت الإجابات ونسب الإنجاز، يتم الاحتكام إلى الوقت، الأسرع في إرسال الإجابة هو الأعلى.
        </p>
        <LazyComponent fallback={<Loader />} threshold={0.1} rootMargin="100px">
          <Suspense fallback={<Loader />}>
            {leaderboardLoading ? (
              <Loader />
            ) : leaderboardSlides.length > 0 ? (
              <EmblaCarousel
                sectionType="leaderboard"
                link="#"
                data={leaderboardSlides}
                controls={false}
                img={{ size: 'thumbnail', width: 80, height: 80, class: 'rounded-circle mb-3' }}
                count={12}
                options={{ align: 'start', containScroll: false, slidesToScroll: 'auto', direction: 'rtl', loop: false }}
                id="weekly-leaderboard"
                classes="embla-ships ms-0"
                section={{ title: true, content: false, bg: false }}
              />
            ) : (
              <p className="text-muted ms-2">لا توجد بيانات للوحة المتصدرين هذا الأسبوع.</p>
            )}
          </Suspense>
        </LazyComponent>
      </div> */}

      <div className="d-flex flex-column flex-sm-row gap-2 gap-sm-3 mb-4" role="tablist" aria-label="أقسام الاختبارات">
        <button
          type="button"
          role="tab"
          aria-selected={mainTab === 'quizzes'}
          aria-controls="quizzes-list-panel"
          id="tab-quizzes-list"
          className={`btn btn-lg rounded-pill flex-grow-1 fw-semibold px-4 py-3 ${mainTab === 'quizzes' ? 'btn-primary' : 'btn-outline-dark'}`}
          onClick={() => setMainTab('quizzes')}
        >
          <i className="bi bi-grid-3x3-gap me-2 d-none d-sm-inline" aria-hidden="true" />
          قائمة الاختبارات
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mainTab === 'results'}
          aria-controls="quizzes-results-panel"
          id="tab-quizzes-results"
          className={`btn btn-lg rounded-pill flex-grow-1 fw-semibold px-4 py-3 ${mainTab === 'results' ? 'btn-dark' : 'btn-outline-dark'}`}
          onClick={() => setMainTab('results')}
        >
          <i className="bi bi-journal-check me-2 d-none d-sm-inline" aria-hidden="true" />
          نتائج الاختبارات
        </button>
      </div>

      <div className="tab-content">
        {mainTab === 'quizzes' && (
          <div
            id="quizzes-list-panel"
            role="tabpanel"
            aria-labelledby="tab-quizzes-list"
            className="tab-pane fade show active"
          >
            <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-3">
              <div className="d-flex gap-2 flex-wrap">
                {categories.map((category) => (
                  <button
                    key={category}
                    type="button"
                    onClick={() => setSelectedCategory(category)}
                    className={`btn rounded-pill px-4 py-2 ${selectedCategory === category
                      ? 'btn-primary'
                      : 'btn-outline-secondary'
                      }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
              <div className="d-flex gap-2">
                <input
                  type="text"
                  placeholder="ابحث عن اختبار..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="form-control rounded-pill mb-0"
                  style={{ maxWidth: '250px' }}
                />
              </div>
            </div>

            {loading && (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">جاري التحميل...</span>
                </div>
                <p className="mt-3 text-muted">جاري تحميل الاختبارات...</p>
              </div>
            )}

            {error && !loading && (
              <div className="alert alert-warning" role="alert">
                <i className="bi bi-exclamation-triangle me-2" aria-hidden="true" />
                {error}
                <br />
                <small>يتم عرض البيانات المحلية كبديل</small>
              </div>
            )}

            {!loading && (
              <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-4">
                {filteredQuizzes.length > 0 ? (
                  filteredQuizzes.map((quiz) => (
                    !quiz.ramadan && !quiz.preview && (
                      <div key={quiz.id} className="col">
                        <QuizCard quiz={quiz} />
                      </div>
                    )
                  ))
                ) : (
                  <div className="col-12 text-center py-5">
                    <p className="text-muted">لا يوجد اختبارات متاحة في هذه الفئة حالياً.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {mainTab === 'results' && (
          <div
            id="quizzes-results-panel"
            role="tabpanel"
            aria-labelledby="tab-quizzes-results"
            className="tab-pane fade show active quiz-results-panel"
          >
            {!hasAuthToken ? (
              <div className="text-center py-5 px-3">
                <p className="text-muted mb-3">سجّل الدخول لعرض نتائج محاولاتك والنسب دون عرض الإجابات.</p>
                <Link href="/login" className="btn btn-primary rounded-pill px-4">
                  تسجيل الدخول
                </Link>
              </div>
            ) : (
              <section className="pb-4" aria-labelledby="my-quiz-attempts-heading">
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
                  <h2 id="my-quiz-attempts-heading" className="h4 mb-0">
                    <i className="bi bi-clock-history me-2" aria-hidden="true" />
                    محاولاتي
                  </h2>
                  <FloatingBugReport
                    showFloatingButton={false}
                    showInlineTrigger
                    inlineTriggerClassName="btn btn-outline-secondary btn-sm"
                    inlineTriggerLabel="الإبلاغ عن مشكلة"
                  />
                </div>
                <p className="text-muted small mb-3">
                  عرض النتائج والنسب فقط — لا تُعرض الإجابات من هذه القائمة.
                </p>
                <div className="d-flex flex-wrap gap-2 mb-3">
                  {[
                    { key: 'all', label: 'الكل' },
                    { key: 'passed', label: 'نجحت' },
                    { key: 'failed', label: 'لم أجتز' },
                  ].map(({ key, label }) => (
                    <button
                      key={key}
                      type="button"
                      className={`btn btn-sm rounded-pill px-3 ${trialsFilter === key ? 'btn-primary' : 'btn-outline-secondary'}`}
                      onClick={() => {
                        setAttemptsPage(1);
                        setTrialsFilter(key);
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                {myAttemptsLoading ? (
                  <div className="text-center py-4">
                    <div className="spinner-border spinner-border-sm text-primary" role="status">
                      <span className="visually-hidden">جاري التحميل...</span>
                    </div>
                  </div>
                ) : myAttemptsError ? (
                  <div className="alert alert-warning mb-0" role="alert">
                    {myAttemptsError}
                  </div>
                ) : myAttempts.length === 0 ? (
                  <p className="text-muted mb-0">لا توجد محاولات ضمن التصفية الحالية.</p>
                ) : (
                  <>
                    <div className="table-responsive rounded-3 border quiz-attempts-table">
                      <table className="table table-hover align-middle mb-0">
                        <thead className="table-light">
                          <tr>
                            <th scope="col" className="text-center" style={{ width: '3rem' }}>الحالة</th>
                            <th scope="col">الاختبار</th>
                            <th scope="col" className="text-nowrap">النتيجة</th>
                            <th scope="col" className="text-nowrap d-none d-md-table-cell">التاريخ</th>
                          </tr>
                        </thead>
                        <tbody>
                          {myAttempts.map((row) => {
                            const when = row.completedAt
                              ? new Date(row.completedAt).toLocaleString('ar-EG', {
                                dateStyle: 'medium',
                                timeStyle: 'short',
                              })
                              : '—';
                            const scoreLine = `${row.scoreObjective ?? 0} / ${row.objectiveMaxScore ?? 0}`;
                            const pct = Math.round(Number(row.percentageObjective) || 0);
                            return (
                              <tr key={row.attemptId}>
                                <td className="text-center">
                                  {row.passed ? (
                                    <span className="d-inline-flex align-items-center justify-content-center rounded-circle text-success" title="نجحت" aria-label="نجحت">
                                      <i className="bi bi-check-circle-fill" style={{ fontSize: '1.35rem' }} aria-hidden="true" />
                                    </span>
                                  ) : (
                                    <span className="d-inline-flex align-items-center justify-content-center text-danger" title="لم تنجح" aria-label="لم تنجح">
                                      <i className="bi bi-x-circle-fill" style={{ fontSize: '1.35rem' }} aria-hidden="true" />
                                    </span>
                                  )}
                                </td>
                                <td>
                                  <Link href={`/services/quizzes/${row.quizId}`} className="fw-semibold text-decoration-none">
                                    {row.quizTitle || 'اختبار'}
                                  </Link>
                                  {row.pendingManualReview && (
                                    <span className="badge bg-warning text-dark ms-2">قيد المراجعة</span>
                                  )}
                                </td>
                                <td className="text-nowrap">
                                  <span className="fw-bold">{pct}%</span>
                                  <span className="text-muted small d-block">{scoreLine} نقطة</span>
                                </td>
                                <td className="text-muted small d-none d-md-table-cell">{when}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {myAttemptsTotalPages > 1 && (
                      <div className="d-flex justify-content-between align-items-center mt-3 quiz-attempts-pagination">
                        <span className="text-muted small">
                          {myAttemptsTotal} محاولة
                        </span>
                        <div className="btn-group" role="group" aria-label="تصفح المحاولات">
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            disabled={attemptsPage <= 1}
                            onClick={() => setAttemptsPage((p) => Math.max(1, p - 1))}
                          >
                            السابق
                          </button>
                          <span className="btn btn-sm btn-light disabled">
                            {attemptsPage} / {myAttemptsTotalPages}
                          </span>
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            disabled={attemptsPage >= myAttemptsTotalPages}
                            onClick={() => setAttemptsPage((p) => p + 1)}
                          >
                            التالي
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </section>
            )}
          </div>
        )}
      </div>
    </main>
  );
}
